import React, { useEffect, useState } from 'react';
import { getPresence, PresenceInfo } from '../utils/secureFunctions';

// Shared presence store: every <OnlineDot> on screen is batched into one request, refreshed every 30s.
const cache = new Map<string, PresenceInfo>();
const watchers = new Map<string, Set<() => void>>();
let timer: number | null = null;
let poller: number | null = null;

function notify(ids: string[]) {
  ids.forEach((id) => watchers.get(id)?.forEach((fn) => fn()));
}

async function refresh() {
  timer = null;
  const ids = Array.from(watchers.keys());
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    try {
      const res = await getPresence(chunk);
      chunk.forEach((id) => cache.set(id, res.presence[id] || { online: false, lastSeen: null }));
      notify(chunk);
    } catch { /* keep last known state */ }
  }
}

function schedule() {
  if (timer === null) timer = window.setTimeout(refresh, 60);
  if (poller === null) {
    poller = window.setInterval(() => {
      if (!watchers.size) { window.clearInterval(poller!); poller = null; return; }
      if (document.visibilityState === 'visible') void refresh();
    }, 30_000);
  }
}

export function usePresence(userId?: string | null): PresenceInfo | null {
  const [, force] = useState(0);
  useEffect(() => {
    if (!userId) return undefined;
    const fn = () => force((n) => n + 1);
    if (!watchers.has(userId)) watchers.set(userId, new Set());
    watchers.get(userId)!.add(fn);
    if (!cache.has(userId)) schedule();
    else if (poller === null) schedule();
    return () => {
      const set = watchers.get(userId);
      set?.delete(fn);
      if (set && !set.size) watchers.delete(userId);
    };
  }, [userId]);
  return userId ? cache.get(userId) || null : null;
}

export function formatLastSeen(p: PresenceInfo | null): string {
  if (!p) return '';
  if (p.online) return 'Online';
  if (!p.lastSeen) return '';
  const mins = Math.max(1, Math.round((Date.now() - new Date(p.lastSeen).getTime()) / 60000));
  if (mins < 60) return `Active ${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `Active ${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return days <= 7 ? `Active ${days}d ago` : '';
}

/** Small green dot shown on an avatar while the user is online. Parent must be position: relative. */
const OnlineDot: React.FC<{ userId?: string | null; size?: number; style?: React.CSSProperties }> = ({ userId, size = 12, style }) => {
  const p = usePresence(userId);
  if (!p?.online) return null;
  return (
    <span
      aria-label="Online"
      title="Online"
      style={{
        position: 'absolute',
        right: 0,
        bottom: 0,
        width: size,
        height: size,
        borderRadius: '50%',
        background: '#22c55e',
        border: '2px solid var(--color-surface, #fff)',
        boxSizing: 'border-box',
        pointerEvents: 'none',
        ...style,
      }}
    />
  );
};

/** "Online" / "Active 5m ago" line under a name. */
export const PeerStatus: React.FC<{ userId?: string | null; style?: React.CSSProperties }> = ({ userId, style }) => {
  const p = usePresence(userId);
  const label = formatLastSeen(p);
  if (!label) return null;
  return (
    <span style={{ fontSize: 12, fontWeight: 500, color: p?.online ? '#16a34a' : 'var(--color-text-secondary, #6b7280)', ...style }}>
      {p?.online ? '● ' : ''}{label}
    </span>
  );
};

export default OnlineDot;
